/* eslint-disable max-len */
import React, { FC, useEffect, useMemo, useState } from 'react'
import Modal from '../common/Modal'
import { format, nowDate, secToHour } from '../../../utils/date'
import Holidays from 'date-holidays'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import LoadingSquare from '../common/LoadingSquare'
import DateCalendarModule from './DateCalendarModule'
import { useAlert } from 'react-alert'
import AddMemberModule from './AddMemberModule'

interface ConfigData {
  id: number
  name: string
  color: string
  schedule: {
    start_date: string | null
    end_date: string | null
    time: number
  }
  member_list: {
    member_id: number
    name: string
    kana: string
    profile_image: string | null
  }[]
  parent_id?: number
  member_ids?: number[]
}

interface MemberDetail {
  member_id: number
  name: string
  kana: string | null
  profile_image: string | null
}

interface UpdateParams {
  blockType: string
  id: number
  parentId?: number
  memberIds?: number[]
  startDate: string | null
  endDate: string | null
  time: number
  callback: VoidFunction
}

interface Props {
  closeCallback: VoidFunction
  updateCallback: (params: UpdateParams) => void
  isDisplay: boolean
  blockType: string
  configData: ConfigData | undefined
  isLoading?: boolean
  assignedMemberIds?: number[]
}

const GanttSettingModal: FC<Props> = (props) => {
  const {
    closeCallback,
    updateCallback,
    isDisplay,
    blockType,
    configData,
    isLoading,
    assignedMemberIds,
  } = props

  const alert = useAlert()

  const holidays = new Holidays()
  holidays.init('JP')

  const [scheduledStartDate, setScheduledStartDate] = useState<string | null>(null)
  const [scheduledEndDate, setScheduledEndDate] = useState<string | null>(null)
  const [scheduledTime, setScheduledTime] = useState<string>("")
  const [selectMembers, setSelectMembers] = useState<MemberDetail[]>([])

  const [isInputSchedule, setIsInputSchedule] = useState<boolean>(false)

  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false)

  const error = useMemo(() => {
    if (isInputSchedule && Number(format(scheduledStartDate, "YYYYMMDD")) > Number(format(scheduledEndDate, "YYYYMMDD"))) {
      return "終了予定が開始予定よりも前に設定されています"
    }
    return ""
  }, [scheduledStartDate, scheduledEndDate, isInputSchedule])

  const changeScheduledTime = (time: string) => {
    setScheduledTime(time)
  }

  const formatScheduledTime = () => {
    const tmpVal = scheduledTime.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0)).replace(/[^0-9]/g, '')
    setScheduledTime(Number(tmpVal).toString())
  }

  const setMember = (members: MemberDetail[]) => {
    setSelectMembers(members)
  }

  const delMember = (memberId: number) => {
    setSelectMembers([...selectMembers.filter((v) => v.member_id !== memberId)])
  }

  const closeModal = () => {
    closeCallback()
  }

  const buildIcon = () => {
    if (blockType === "label_group_category") return "folder"
    if (blockType === "label_group") return "square"
    if (blockType === "label") return "circle"
    return "circle"
  }

  const submit = () => {
    setIsLoadingSubmit(true)
    const tmpParams:UpdateParams = {
      blockType: blockType,
      id: configData?.id || 0,
      startDate: isInputSchedule ? scheduledStartDate : null,
      endDate: isInputSchedule ? scheduledEndDate : null,
      time: Number(scheduledTime) * 3600,
      callback: () => {
        closeCallback()
        setIsLoadingSubmit(false)
        alert.success(`${configData?.name}の情報を更新しました`)
      },
    }
    if (blockType === "label") {
      tmpParams.memberIds = selectMembers.map((v) => v.member_id)
      tmpParams.parentId = configData?.parent_id
    }
    if (blockType === "member") {
      tmpParams.parentId = configData?.parent_id
    }
    updateCallback(tmpParams)
  }

  useEffect(() => {
    if (!configData) return
    setScheduledStartDate(configData.schedule.start_date || nowDate("YYYY-MM-DD"))
    setScheduledEndDate(configData.schedule.end_date || nowDate("YYYY-MM-DD"))
    setIsInputSchedule(!!configData.schedule.end_date)
    setScheduledTime(secToHour(configData.schedule.time).toString())
    if (blockType === "label") {
      setSelectMembers(configData?.member_list.filter((v) => assignedMemberIds?.includes(v.member_id)))
    } else {
      setSelectMembers([])
    }
  }, [configData])

  return (
    <Modal
      show={isDisplay}
      wrapperClass="gantt_modal w900"
      closeCallback={closeModal}
      closeable
    >
      <>
        {
          isLoading ? (
            <>
              <LoadingSquare>
                <>
                  設定を取得しています…
                </>
              </LoadingSquare>
            </>
          ) : (
            <>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "4px 8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginBottom: "16px",
                  color: "#666666",
                }}
              >
                {
                  blockType !== "member" && (
                    <FontAwesomeIcon
                      icon={['fas', buildIcon()]}
                      className="icon"
                      style={{
                        color: configData?.color,
                        fontSize: "14px",
                      }}
                    />
                  )
                }
                {`${configData?.name}${blockType === "member" ? "さん" : ""} 予定期間・予定工数設定`}
              </div>
              <fieldset>
                <legend>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    予定期間
                    <FontAwesomeIcon
                      icon={['fas', isInputSchedule ? 'toggle-on' : 'toggle-off']}
                      onClick={() => setIsInputSchedule(!isInputSchedule)}
                      style={{
                        color: isInputSchedule ? "#0b9554" : "#999999",
                        fontSize: "20px",
                      }}
                    />
                  </div>
                </legend>
                <div className="item w50">
                  <div
                    className="title"
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    開始予定
                  </div>
                  <div className="detail equal_space row_1" style={{ position: "relative" }}>
                    {
                      isInputSchedule ? (
                        <DateCalendarModule
                          date={scheduledStartDate || nowDate("YYYY-MM-DD")}
                          changeCallback={(callback_date) => {
                            setScheduledStartDate(callback_date)
                          }}
                          disabled={false}
                          future
                          omit
                        />
                      ) : (
                        <div
                          onClick={() => setIsInputSchedule(true)}
                          role="none"
                        >
                          開始予定設定なし
                          <span className="link">
                            開始予定を設定
                          </span>
                        </div>
                      )
                    }
                  </div>
                </div>
                <div className="item w50">
                  <div
                    className="title"
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    終了予定
                  </div>
                  <div className="detail equal_space row_1" style={{ position: "relative" }}>
                    {
                      isInputSchedule ? (
                        <DateCalendarModule
                          date={scheduledEndDate || nowDate("YYYY-MM-DD")}
                          changeCallback={(callback_date) => {
                            setScheduledEndDate(callback_date)
                          }}
                          disabled={false}
                          future
                          omit
                        />
                      ) : (
                        <div
                          onClick={() => setIsInputSchedule(true)}
                          role="none"
                        >
                          終了予定設定なし
                          <span className="link">
                            終了予定を設定
                          </span>
                        </div>
                      )
                    }
                  </div>
                </div>
                {
                  !!error && (
                    <div
                      style={{
                        color: "#FF0000",
                      }}
                    >
                      {error}
                    </div>
                  )
                }
              </fieldset>
              <fieldset>
                <legend>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    予定工数
                  </div>
                </legend>
                <div className="item w50">
                  <div
                    className="title"
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    工数
                  </div>
                  <div className="detail row_1" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <input
                      type="text"
                      value={scheduledTime}
                      onChange={(e) => changeScheduledTime(e.target.value)}
                      onBlur={() => formatScheduledTime()}
                      onKeyPress={(e) => e.key === "Enter" && formatScheduledTime()}
                      style={{ width: "auto" }}
                    />
                    時間
                  </div>
                </div>
              </fieldset>
              {
                blockType === "label" && (
                  <fieldset>
                    <legend>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        担当メンバー
                      </div>
                    </legend>
                    <div className="item w100">
                      <div
                        style={{
                          border: "1px solid #cccccc",
                          borderRadius: "4px",
                          padding: "16px",
                          width: "100%",
                          backgroundColor: "#FFFFFF",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "8px",
                          }}
                        >
                          {
                            selectMembers.map((value) => (
                              <div
                                className="member_block"
                                key={`category_member_${value.member_id}`}
                                style={{
                                  backgroundColor: "#ffffff",
                                  border: "1px solid #cccccc",
                                  borderRadius: "20px",
                                  padding: "4px 8px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                {
                                  value.profile_image ? (
                                    <img
                                      alt="profile"
                                      src={value.profile_image}
                                      className="profile_icon"
                                      style={{
                                        borderRadius: "50%",
                                        width: "20px",
                                      }}
                                    />
                                  ) : (
                                    <img
                                      alt=""
                                      src={`/static/images/profile/${(Number(value.member_id) || 0) % 12}.jpeg`}
                                      className="profile_icon"
                                      style={{
                                        borderRadius: "50%",
                                        width: "20px",
                                      }}
                                    />
                                  )
                                }
                                {value.name}
                                <span
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() => delMember(value.member_id)}
                                  role="none"
                                >
                                  <FontAwesomeIcon icon={['fas', 'times']} />
                                </span>
                              </div>
                            ))
                          }
                          <AddMemberModule
                            memberIdList={selectMembers.map((v) => v.member_id)}
                            callbackMembers={(val) => setMember(configData?.member_list.filter((v) => val.map((w) => w.id).includes(v.member_id)) || [])}
                            memberList={configData?.member_list.map((v) => ({ id: v.member_id, name: v.name })) || []}
                          />
                        </div>
                      </div>
                    </div>
                  </fieldset>
                )
              }
              <div className="button_area">
                <button
                  type="button"
                  className="button cancel"
                  onClick={() => closeCallback()}
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  className="button"
                  onClick={submit}
                  disabled={isLoadingSubmit || !!error}
                >
                  {
                    isLoadingSubmit ? (
                      <>
                        <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                      </>
                    ) : (
                      <>
                        更新
                      </>
                    )
                  }
                </button>
              </div>
            </>
          )
        }
      </>
    </Modal>
  )
}

GanttSettingModal.defaultProps = {
  assignedMemberIds: [],
  isLoading: false,
}

export default GanttSettingModal
